/**
 * Core Module
 *
 * Singleton services shared across the entire application:
 *  - AuthenticationService (Angular 18) — registered via @Injectable, providedIn: 'root'
 *  - PermissionsService (Angular 18) — registered via @Injectable, providedIn: 'root'
 *  - CsrfInterceptor — attaches CSRF token to mutating HTTP requests
 *  - Bridges to AngularJS services that have not been migrated yet:
 *      TENANT_PORT, FEATURE_TOGGLE_PORT (used by AuthenticationService)
 *      ROOT_SCOPE_PORT, CONFIG_PORT, STORAGE_PORT, MD5_PORT (used by PermissionsService)
 *
 * Import this module ONLY in AppModule.
 */
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { CsrfInterceptor } from './auth/csrf.interceptor';
import { FEATURE_TOGGLE_PORT } from './auth/feature-toggle.port';
import { TENANT_PORT } from './auth/tenant.port';
import { CONFIG_PORT } from './permissions/config.port';
import { MD5_PORT } from './permissions/md5.port';
import { ROOT_SCOPE_PORT } from './permissions/root-scope.port';
import { STORAGE_PORT } from './permissions/storage.port';

type AjsInjector = { get: (name: string) => unknown };

@NgModule({
  imports: [CommonModule, HttpClientModule],
  providers: [
    // CSRF interceptor — replaces legacy $http.defaults.headers
    { provide: HTTP_INTERCEPTORS, useExisting: CsrfInterceptor, multi: true },

    // Bridges to AngularJS services (resolved lazily via $injector)
    {
      provide: TENANT_PORT,
      useFactory: (i: AjsInjector) => i.get('TenantService'),
      deps: ['$injector'],
    },
    {
      provide: FEATURE_TOGGLE_PORT,
      useFactory: (i: AjsInjector) => i.get('FeatureToggleService'),
      deps: ['$injector'],
    },
    {
      provide: ROOT_SCOPE_PORT,
      useFactory: (i: AjsInjector) => i.get('$rootScope'),
      deps: ['$injector'],
    },
    {
      provide: CONFIG_PORT,
      useFactory: (i: AjsInjector) => i.get('ConfigService'),
      deps: ['$injector'],
    },
    {
      provide: STORAGE_PORT,
      useFactory: (i: AjsInjector) => i.get('StorageService'),
      deps: ['$injector'],
    },
    {
      provide: MD5_PORT,
      useFactory: (i: AjsInjector) => i.get('md5'),
      deps: ['$injector'],
    },
  ],
})
export class CoreModule {}
